import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  deleteColumn,
  deleteRow,
  deleteTable,
  ELEMENT_TABLE,
  focusEditor,
  insertTableColumn,
  insertTableRow,
  someNode,
  usePlateEditorState,
} from '@udecode/plate';

import { Item } from 'components/text_editor_new/components/controls/controls_new';
import OutsideClickHandler from 'components/common/utilities/outside_click_handler';
import PopoverMenu, { PopoverMenuItem, usePopoverMenu } from 'components/common/menu';

// Contextual table controls for the composer toolbar. Rendered only when the caret is inside a table,
// so it stays out of the way otherwise. Uses the shared PopoverMenu (portaled) rather than controls
// anchored to the table itself — the editor's `overflow` clipping would hide anything positioned
// around the table, and the menu's portal sidesteps that entirely.
export default function TableMenu({ editorId }) {
  const { t } = useTranslation();
  const editor = usePlateEditorState(editorId);
  const { targetRef, isOpen, onClose, onToggle, setTargetRef } = usePopoverMenu();

  const isInTable = !!editor?.selection && someNode(editor, { match: { type: ELEMENT_TABLE } });
  if (!isInTable) return null;

  const chevronDir = isOpen ? 'up' : 'down';

  return (
    <OutsideClickHandler onClickOutside={onClose}>
      <ExpandableToolbarItem
        data-aid="tableMenuButton"
        isActive={isOpen}
        onClick={onToggle}
        ref={setTargetRef}
        title={t('Table')}
      >
        <span>
          <i className="fa fa-table" />
          <i className={`draftStylesMenu-item-chevron fa fa-angle-${chevronDir}`} />
        </span>
      </ExpandableToolbarItem>
      <PopoverMenu
        boundByWindow
        data-aid="tableMenu"
        isOpen={isOpen}
        onClickOutside={null}
        onClose={onClose}
        position="bottom"
        targetRef={targetRef}
      >
        <PopoverMenuItem data-aid="insertRow" onClick={() => run(() => insertTableRow(editor))}>
          {t('Insert row below')}
        </PopoverMenuItem>
        <PopoverMenuItem data-aid="insertColumn" onClick={() => run(() => insertTableColumn(editor))}>
          {t('Insert column right')}
        </PopoverMenuItem>
        <PopoverMenuItem data-aid="deleteRow" onClick={() => run(() => deleteRow(editor))}>
          {t('Delete row')}
        </PopoverMenuItem>
        <PopoverMenuItem data-aid="deleteColumn" onClick={() => run(() => deleteColumn(editor))}>
          {t('Delete column')}
        </PopoverMenuItem>
        <PopoverMenuItem data-aid="deleteTable" onClick={() => run(() => deleteTable(editor))}>
          {t('Delete table')}
        </PopoverMenuItem>
      </PopoverMenu>
    </OutsideClickHandler>
  );

  // Run a table transform, close the menu, and restore focus to the editor. The transforms act on the
  // current selection (the cell the caret is in), which Slate keeps even while the menu has DOM focus.
  function run(action) {
    action();
    onClose();
    focusEditor(editor);
  }
}

const ExpandableToolbarItem = styled(Item)`
  width: 40px;

  & > span {
    align-items: center;
    display: flex;
    justify-content: center;
    white-space: nowrap;
  }
`;

TableMenu.propTypes = {
  editorId: PropTypes.string.isRequired,
};
